import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save } from "lucide-react";

const FIELDS = [
  { key: "story", label: "My story", type: "textarea" },
  { key: "dreams", label: "My dreams", type: "textarea" },
  { key: "happy_place", label: "Happy place", type: "text" },
  { key: "personal_motto", label: "Personal motto", type: "text" },
  { key: "favourite_quote", label: "Favourite quote", type: "text" },
  { key: "lifestyle", label: "Lifestyle", type: "text" },
  { key: "career", label: "Career", type: "text" },
  { key: "family", label: "Family", type: "text" },
  { key: "pets", label: "Pets", type: "text" },
  { key: "health", label: "Health & wellness", type: "text" },
  { key: "faith", label: "Faith / spirituality (optional)", type: "text" },
];

const SELECTS = [
  { key: "relationship_goals", label: "Relationship goals", options: ["Life partner", "Serious long-term", "Someone to grow with"] },
  { key: "children", label: "Children", options: ["I have and want more", "I have and don't want more", "None yet, want them", "Not sure", "Prefer no children"] },
  { key: "love_language", label: "Love language", options: ["Words of affirmation", "Quality time", "Acts of service", "Physical touch", "Gifts"] },
  { key: "communication", label: "Communication", options: ["Talk it through calmly", "Take space, then talk", "Direct and quick", "Write it out first"] },
  { key: "conflict_style", label: "Conflict style", options: ["Collaborative", "Compromise", "Take a break", "Address directly"] },
];

export default function Profile() {
  const { user, refresh } = useAuth();
  const [p, setP] = useState({});
  const [values, setValues] = useState([]);
  const [allValues, setAllValues] = useState([]);
  const [bucket, setBucket] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user) { setP(user.profile || {}); setValues(user.profile?.values || []); }
    api.get("/profile/values").then((r) => setAllValues(r.data.values));
  }, [user]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const toggleValue = (v) => setValues((cur) => cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
  const addBucket = () => { const t = bucket.trim(); if (!t) return; setP((prev) => ({ ...prev, bucket_list: [...(prev.bucket_list || []), t] })); setBucket(""); };
  const removeBucket = (i) => setP((prev) => ({ ...prev, bucket_list: (prev.bucket_list || []).filter((_, k) => k !== i) }));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { ...p, values, adventure_level: p.adventure_level ? Number(p.adventure_level) : undefined };
      await api.put("/profile", payload);
      await refresh();
      toast.success("Life profile saved.");
    } catch (e) { setErr(formatApiError(e)); toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-rose">Your life profile</div>
        <h1 className="font-display text-5xl text-navy leading-tight" data-testid="profile-title">Who you are, in your own words.</h1>
        <p className="text-navy/70 mt-2">Write like you're talking to a kind friend, not a recruiter.</p>
      </div>

      <div className="glass rounded-3xl p-8 space-y-6">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-xs uppercase tracking-widest text-navy/60">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea data-testid={`p-${f.key}`} value={p[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} rows={4}
                className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/80 border border-navy/10" />
            ) : (
              <input data-testid={`p-${f.key}`} value={p[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/80 border border-navy/10" />
            )}
          </div>
        ))}

        {SELECTS.map((s) => (
          <div key={s.key}>
            <label className="text-xs uppercase tracking-widest text-navy/60">{s.label}</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {s.options.map((o) => (
                <button key={o} type="button" data-testid={`p-${s.key}-${o}`} onClick={() => set(s.key, o)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-all duration-500 ${
                    p[s.key] === o ? "bg-navy text-cream-100 border-navy" : "bg-white/70 border-navy/10 text-navy hover:bg-white"
                  }`}>{o}</button>
              ))}
            </div>
          </div>
        ))}

        <div>
          <label className="text-xs uppercase tracking-widest text-navy/60">Adventure level (1–5)</label>
          <div className="mt-2 flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`p-adv-${n}`} onClick={() => set("adventure_level", n)}
                className={`flex-1 py-3 rounded-2xl font-display text-xl ${
                  Number(p.adventure_level) === n ? "bg-navy text-cream-100" : "bg-white/70 text-navy hover:bg-white"
                }`}>{n}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-navy/60">Values</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {allValues.map((v) => (
              <button key={v} type="button" data-testid={`p-val-${v}`} onClick={() => toggleValue(v)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-all duration-500 ${
                  values.includes(v) ? "bg-rose text-white border-rose" : "bg-white/70 border-navy/10 text-navy hover:bg-white"
                }`}>{v}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-navy/60">Bucket list</label>
          <div className="mt-2 flex gap-2">
            <input data-testid="p-bucket-input" value={bucket} onChange={(e) => setBucket(e.target.value)}
              placeholder="Learn to sail…" className="flex-1 px-4 py-3 rounded-2xl bg-white/80 border border-navy/10" />
            <button type="button" data-testid="p-bucket-add" onClick={addBucket} className="btn-pill bg-navy text-cream-100 hover:bg-navy-600">Add</button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {(p.bucket_list || []).map((b, i) => (
              <span key={i} className="text-sm px-3 py-1.5 rounded-full bg-sage/15 text-sage-500 flex items-center gap-2">
                {b} <button data-testid={`p-bucket-rm-${i}`} onClick={() => removeBucket(i)} className="text-sage/60 hover:text-sage">×</button>
              </span>
            ))}
          </div>
        </div>

        {err && <div className="text-sm text-rose-600" data-testid="profile-error">{err}</div>}

        <div className="flex justify-end pt-2">
          <button data-testid="profile-save" onClick={save} disabled={busy}
            className="btn-pill bg-rose text-white hover:bg-rose-600 hover:scale-[1.02] flex items-center gap-2 disabled:opacity-60">
            <Save size={16} /> {busy ? "Saving..." : "Save profile"}
          </button>
        </div>
      </div>
    </div>
  );
}
